import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import cmsClient from "src/services/cmsClient";
import type { TrackableSurface } from "../utils/eventNames";

export interface ResolvedEntity {
	title: string;
	entity_id: number;
}

export type ResolverResponse = {
	resolved: Partial<Record<TrackableSurface, Record<string, ResolvedEntity>>>;
};

export type ResolverInput = Partial<Record<TrackableSurface, string[]>>;

const buildPayload = (input: ResolverInput) => ({
	surfaces: (Object.keys(input) as TrackableSurface[])
		.map((surface) => ({
			surface,
			ids: Array.from(new Set(input[surface] ?? [])).sort()
		}))
		.filter((g) => g.ids.length > 0)
});

/**
 * Batch resolves raw `entity_id` values from tracking events back
 * to their human-readable title + numeric admin-route id, so the
 * analytics tabs can render meaningful labels and clickable rows
 * instead of opaque uuids/longs.
 *
 * Pass a map of surface → ids (e.g. `{ PROMOTION: ["1", "2"] }`).
 * The query key is stable across re-renders because ids are
 * de-duplicated and sorted before stringification.
 */
export const useEntityResolver = (
	input: ResolverInput,
	options?: { enabled?: boolean }
) => {
	const payload = buildPayload(input);
	const enabled = (options?.enabled ?? true) && payload.surfaces.length > 0;

	return useQuery({
		queryKey: ["analytics", "resolve-entities", payload],
		queryFn: async () => {
			const { data } = await cmsClient.post<ResolverResponse>(
				"/tracking/resolve-entities/",
				payload
			);
			return data ?? { resolved: {} };
		},
		enabled,
		staleTime: 5 * 60 * 1000
	});
};

/**
 * Convenience: resolve a single (surface, ids) pair and pluck the
 * surface's lookup map back out. Returns `{}` while loading or if
 * the surface has no resolved entries.
 */
export const useSingleSurfaceResolver = (
	surface: TrackableSurface,
	ids: string[],
	options?: { enabled?: boolean }
) => {
	const query = useEntityResolver({ [surface]: ids } as ResolverInput, options);
	// Memoise so the consumer's row-building useMemo (which lists
	// `lookup` in its dep array) doesn't re-run every render when
	// the underlying query data is unchanged.
	const lookup = useMemo(
		() => query.data?.resolved?.[surface] ?? {},
		[query.data, surface]
	);
	return {
		lookup,
		isLoading: query.isLoading,
		isFetching: query.isFetching,
		isError: query.isError
	};
};
